import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../../../components/Button';
import Select from '../../../../components/Select';
import ModuleSection from '../ModuleSection';
import ArticleEditModal from '../ArticleEditModal';
import blogStorage from '../../utils/storage';
import './index.scss';

const ArticleList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('publishedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // 检查URL参数，如果有编辑参数就打开编辑模态框
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && articles.length > 0) {
      const article = articles.find(art => art.id === editId);
      if (article) {
        setEditingArticleId(editId);
        setEditModalOpen(true);
        // 清除URL参数
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('edit');
          return newParams;
        });
      }
    }
  }, [articles, searchParams, setSearchParams]);

  useEffect(() => {
    filterAndSortArticles();
  }, [articles, searchQuery, selectedCategory, sortBy, sortOrder]);

  const loadData = () => {
    const articlesData = blogStorage.getArticles();
    const categoriesData = blogStorage.getCategories();
    setArticles(articlesData);
    setCategories(categoriesData);
  };

  const filterAndSortArticles = () => {
    let filtered = [...articles];

    // 搜索过滤
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query) ||
        (article.tags && article.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // 分类过滤
    if (selectedCategory) {
      filtered = filtered.filter(article => article.categoryId === selectedCategory);
    }

    // 分离置顶和非置顶文章
    const pinnedArticles = filtered.filter(article => article.isTop === true);
    const regularArticles = filtered.filter(article => article.isTop !== true);

    // 排序函数
    const sortArticles = (articles) => {
      if (!articles || articles.length === 0) return [];
      
      return articles.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // 处理日期字段
        if (sortBy === 'publishedAt' || sortBy === 'updatedAt') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        // 处理数字字段
        if (sortBy === 'viewCount' || sortBy === 'likeCount' || sortBy === 'wordCount') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    };

    // 合并结果：置顶文章始终在前面
    filtered = [
      ...sortArticles(pinnedArticles),
      ...sortArticles(regularArticles)
    ];

    setFilteredArticles(filtered);
    setCurrentPage(1); // 重置到第一页
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleSelectArticle = (articleId) => {
    setSelectedArticles(prev => {
      if (prev.includes(articleId)) {
        return prev.filter(id => id !== articleId);
      } else {
        return [...prev, articleId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedArticles.length === getCurrentPageArticles().length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(getCurrentPageArticles().map(article => article.id));
    }
  };

  const handleDeleteArticle = (articleId) => {
    if (confirm('确定要删除这篇文章吗？')) {
      blogStorage.deleteArticle(articleId);
      loadData();
      setSelectedArticles(prev => prev.filter(id => id !== articleId));
    }
  };

  const handleBatchDelete = () => {
    if (selectedArticles.length === 0) {
      alert('请选择要删除的文章');
      return;
    }

    if (confirm(`确定要删除选中的 ${selectedArticles.length} 篇文章吗？`)) {
      selectedArticles.forEach(articleId => {
        blogStorage.deleteArticle(articleId);
      });
      loadData();
      setSelectedArticles([]);
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticleId(article.id);
    setEditModalOpen(true);
  };

  const handleSaveArticle = (savedArticle) => {
    loadData(); // 重新加载数据
    setEditingArticleId(null);
    setEditModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingArticleId(null);
  };

  const handleViewArticle = (articleId) => {
    navigate(`/blog/article/${articleId}`);
  };

  const handleToggleTop = (articleId) => {
    const article = articles.find(art => art.id === articleId);
    if (article) {
      blogStorage.updateArticle(articleId, { isTop: !article.isTop });
      loadData();
    }
  };

  const getCurrentPageArticles = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredArticles.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredArticles.length / pageSize);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : '未分类';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  return (
    <div className="article-list">
      {/* 筛选和排序 */}
      <ModuleSection
        title="筛选与排序"
        variant="info"
      >
        <div className="filter-controls">
          <div className="filter-left">
            <div className="form-group">
              <label>分类筛选</label>
              <Select
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                options={[
                  { value: '', label: '所有分类' },
                  ...categories.map(category => ({
                    value: category.id,
                    label: category.name
                  }))
                ]}
                placeholder="所有分类"
                dropdownPortal={true}
              />
            </div>
          </div>
          
          <div className="filter-right">
            <div className="sort-controls">
              <label>排序方式</label>
              <div className="sort-buttons">
                <Button
                  size="small"
                  type={sortBy === 'publishedAt' ? 'primary' : undefined}
                  onClick={() => handleSort('publishedAt')}
                >
                  发布时间 {sortBy === 'publishedAt' && (sortOrder === 'desc' ? '↓' : '↑')}
                </Button>
                <Button
                  size="small"
                  type={sortBy === 'viewCount' ? 'primary' : undefined}
                  onClick={() => handleSort('viewCount')}
                >
                  浏览量 {sortBy === 'viewCount' && (sortOrder === 'desc' ? '↓' : '↑')}
                </Button>
                <Button
                  size="small"
                  type={sortBy === 'wordCount' ? 'primary' : undefined}
                  onClick={() => handleSort('wordCount')}
                >
                  字数 {sortBy === 'wordCount' && (sortOrder === 'desc' ? '↓' : '↑')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModuleSection>

      {/* 文章列表 */}
      <ModuleSection
        title="文章列表"
        headerExtra={`共 ${filteredArticles.length} 篇文章`}
        actions={
          <div className="article-header-actions">
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="搜索文章..."
                className="search-input"
              />
            </div>
            {selectedArticles.length > 0 && (
              <Button 
                type="danger"
                onClick={handleBatchDelete}
              >
                批量删除 ({selectedArticles.length})
              </Button>
            )}
          </div>
        }
        variant="list"
        noPadding
      >
        <div className="articles-container">
        {getCurrentPageArticles().length > 0 ? (
          <>
            <div className="list-controls">
              <label className="select-all">
                <input
                  type="checkbox"
                  checked={selectedArticles.length === getCurrentPageArticles().length && getCurrentPageArticles().length > 0}
                  onChange={handleSelectAll}
                />
                全选
              </label>
            </div>
            
            <div className="articles-list">
              {getCurrentPageArticles().map(article => (
                <div key={article.id} className={`blog-card ${article.isTop ? 'topped' : ''}`}>
                  <div className="article-select">
                    <input
                      type="checkbox"
                      checked={selectedArticles.includes(article.id)}
                      onChange={() => handleSelectArticle(article.id)}
                    />
                  </div>
                  
                  <div className="article-content">
                    <div className="article-header">
                      <h4 className="blog-title clickable" onClick={() => handleViewArticle(article.id)}>
                        {article.isTop && <span className="top-badge">置顶</span>}
                        {article.title}
                      </h4>
                      <div className="article-meta">
                        <span className="publish-date blog-text-primary">
                          发布: {formatDate(article.publishedAt)}
                        </span>
                        <span className="category blog-text-secondary">
                          分类: {getCategoryName(article.categoryId)}
                        </span>
                        <span className="stats blog-text-muted">
                          {article.viewCount} 浏览 · {article.likeCount} 点赞 · {article.wordCount} 字
                        </span>
                      </div>
                    </div>
                    
                    <div className="article-summary blog-text-secondary">
                      {article.content.substring(0, 200)}...
                    </div>
                    
                    {article.tags.length > 0 && (
                      <div className="article-tags">
                        {article.tags.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="article-actions">
                    <Button 
                      size="small"
                      onClick={() => handleEditArticle(article)}
                    >
                      编辑
                    </Button>
                    <Button 
                      size="small"
                      type={article.isTop ? 'warning' : undefined}
                      onClick={() => handleToggleTop(article.id)}
                    >
                      {article.isTop ? '取消置顶' : '置顶'}
                    </Button>
                    <Button 
                      size="small"
                      type="danger"
                      onClick={() => handleDeleteArticle(article.id)}
                    >
                      删除
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <p>暂无文章</p>
          </div>
        )}
        </div>

        {/* 分页 */}
        {getTotalPages() > 1 && (
          <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            上一页
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`page-number ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            className="page-btn"
            disabled={currentPage === getTotalPages()}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            下一页
          </button>
          
            <div className="page-info">
              共 {filteredArticles.length} 篇文章，第 {currentPage} / {getTotalPages()} 页
            </div>
          </div>
        )}
      </ModuleSection>

      {/* 编辑模态弹窗 */}
      <ArticleEditModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        mode="edit"
        articleId={editingArticleId}
        onSave={handleSaveArticle}
      />
    </div>
  );
};

export default ArticleList;
